/**
 * WHO HEARS THE ALARM when a reaction is owed.
 *
 * Every client sees every outstanding reaction - that is settled, and it is
 * what `useReactionQueue` is for. This ruling decides only how LOUDLY each
 * client is told: whether the prompt takes over the top of the screen, or sits
 * quietly in the log for anyone who wants to answer it.
 *
 * It is a table ruling rather than a private preference because the answer
 * changes who is expected to speak up. A table where the GM plays every enemy
 * wants the GM woken for all of them; a table where the GM would rather the
 * players notice their own trouble does not. Both tables need the same answer
 * on every screen, or a reaction expires while everyone assumes somebody else
 * was watching.
 *
 * Pure, and zero React dependency.
 */

/** How wide the loud tier reaches. */
export type ReactionAlarmPolicy = 'actorsAndGm' | 'actorsOnly';

export const DEFAULT_REACTION_ALARM: ReactionAlarmPolicy = 'actorsAndGm';

export interface ReactionAlarmEntry {
  key: ReactionAlarmPolicy;
  label: string;
  hint: string;
}

/** The rulings on offer, in the order the GM meets them. */
export const REACTION_ALARM_POLICIES: ReactionAlarmEntry[] = [
  {
    key: 'actorsAndGm',
    label: 'Whoever is acting as them, and the GM',
    hint: 'The GM is woken for every reaction, which is what a table wants when the GM plays the opposition. Everyone else still sees it quietly and may still answer.',
  },
  {
    key: 'actorsOnly',
    label: 'Whoever is acting as them',
    hint: 'Only the player who has picked that character up is interrupted. Quieter for a GM running a large fight, at the cost of noticing an unclaimed character yourself.',
  },
];

export function isReactionAlarmPolicy(value: unknown): value is ReactionAlarmPolicy {
  return value === 'actorsAndGm' || value === 'actorsOnly';
}
